import { useState, useEffect, useRef } from 'react';
import { 
  FaGift, 
  FaCheckCircle, 
  FaUser, 
  FaEnvelope, 
  FaPhone, 
  FaBookOpen, 
  FaClock,
  FaGraduationCap,
  FaStar,
  FaChevronRight,
  FaCalendarAlt
} from 'react-icons/fa';
import { contactInfo } from '../data/siteData';
import { sanitizeFormData, validateEmail, validatePhone, validateText } from '../utils/helpers';
import './FreeTrial.css';

const FreeTrial = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    course: 'noorani-qaida',
    preferredTime: 'morning',
    studentAge: '5-10',
    experience: 'beginner'
  });

  const [focused, setFocused] = useState({});
  const sectionRef = useRef(null);
  const formRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  const courses = [
    'Noorani Qaida',
    'Quran Memorization',
    'Tajweed Course',
    'Quran Reading',
    'Islamic Studies',
    'Arabic Language',
    'Namaz Course',
    'Hadith Course'
  ];

  useEffect(() => {
    // Reset form on component mount
    setFormData({
      name: '',
      email: '',
      phone: '',
      course: 'noorani-qaida',
      preferredTime: 'morning',
      studentAge: '5-10',
      experience: 'beginner'
    });
    setFocused({});
    
    // Reset form using native form reset to clear all fields including dropdowns
    if (formRef.current) {
      formRef.current.reset();
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    // Sanitize input on change
    const sanitizedValue = typeof value === 'string' ? value.trim() : value;
    setFormData({
      ...formData,
      [name]: sanitizedValue
    });
  };

  const handleFocus = (field) => {
    setFocused({ ...focused, [field]: true });
  };

  const handleBlur = (field) => {
    if (!formData[field]) {
      setFocused({ ...focused, [field]: false });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Security: Validate all fields
    const errors = {};
    
    if (!validateText(formData.name, { minLength: 2, maxLength: 100, required: true })) {
      errors.name = 'Please enter a valid name (2-100 characters)';
    }
    
    if (!validateEmail(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }
    
    if (formData.phone && !validatePhone(formData.phone)) {
      errors.phone = 'Please enter a valid phone number (7-15 digits)';
    }
    
    // If there are validation errors, show alert and return
    if (Object.keys(errors).length > 0) {
      alert('Please fix the following errors:\n' + Object.values(errors).join('\n'));
      return;
    }
    
    // Sanitize all form data before processing
    const sanitizedData = sanitizeFormData(formData);
    console.log('Free trial form submitted:', sanitizedData);
    
    // Helper function to format course name
    const formatCourseName = (courseValue) => {
      // Find the course in the courses array
      const course = courses.find(c => 
        c.toLowerCase().replace(/\s+/g, '-') === courseValue
      );
      return course || courseValue.split('-').map(word => 
        word.charAt(0).toUpperCase() + word.slice(1)
      ).join(' ');
    };

    // Helper function to format time
    const formatTime = (timeValue) => {
      const timeMap = {
        'morning': 'Morning (6 AM - 12 PM)',
        'afternoon': 'Afternoon (12 PM - 5 PM)',
        'evening': 'Evening (5 PM - 9 PM)',
        'night': 'Night (9 PM - 12 AM)'
      };
      return timeMap[timeValue] || timeValue;
    };

    // Helper function to format experience
    const formatExperience = (expValue) => {
      const expMap = {
        'beginner': 'Beginner - No prior experience',
        'basic': 'Basic - Can read Arabic letters',
        'intermediate': 'Intermediate - Can read Quran',
        'advanced': 'Advanced - Want to improve'
      };
      return expMap[expValue] || expValue;
    };

    // Create the WhatsApp message using sanitized data
    const message = `*Free Trial Class Request*

*Name:* ${sanitizedData.name}
*Email:* ${sanitizedData.email}
*Phone:* ${sanitizedData.phone || 'Not provided'}
*Student Age:* ${sanitizedData.studentAge} years
*Course Interest:* ${formatCourseName(sanitizedData.course)}
*Preferred Time:* ${formatTime(sanitizedData.preferredTime)}
*Previous Experience:* ${formatExperience(sanitizedData.experience)}

Please contact me to schedule my free trial class.`;

    // Encode the message for URL
    const encodedMessage = encodeURIComponent(message);
    
    // Get WhatsApp number from contactInfo and remove all non-digits
    const whatsappNumber = contactInfo.whatsapp.replace(/[^0-9]/g, '');
    
    // Open WhatsApp with pre-filled message
    const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodedMessage}`;
    window.open(whatsappUrl, '_blank');
    
    alert('Thank you! Opening WhatsApp to send your request...');
    
    // Reset form
    setFormData({
      name: '',
      email: '',
      phone: '',
      course: 'noorani-qaida',
      preferredTime: 'morning',
      studentAge: '5-10',
      experience: 'beginner'
    });
    setFocused({});
    // Reset form using native form reset to clear all fields including dropdowns
    if (formRef.current) {
      formRef.current.reset();
    }
  };

  const benefits = [
    {
      icon: FaGift,
      title: '100% Free',
      description: 'No credit card required. Experience our teaching quality risk-free.'
    },
    {
      icon: FaUser,
      title: 'One-on-One Session',
      description: 'Personalized attention from qualified teachers tailored to your needs.'
    },
    {
      icon: FaClock,
      title: 'Flexible Timing',
      description: 'Choose a time that works best for your schedule, available 24/7.'
    },
    {
      icon: FaStar,
      title: 'Expert Teachers',
      description: 'Learn from certified native Arabic speakers with years of experience.'
    }
  ];

  return (
    <div className="free-trial-page">
      {/* Hero Section */}
      <div className="trial-hero">
        <div className="trial-hero-overlay"></div>
        <div className="container">
          <div className="trial-hero-content">
            <div className="trial-hero-icon-wrapper">
              <FaGift className="trial-hero-icon" />
            </div>
            <span className="trial-badge">Start Learning Today</span>
            <h1 className="trial-hero-title">Book Your Free Trial Class</h1>
            <p className="trial-hero-subtitle">
              Experience our quality teaching and see why thousands of students choose ALNoor Tutors. 
              No credit card required!
            </p>
          </div>
        </div>
      </div>

      {/* Benefits Section */}
      <section className="trial-benefits-section">
        <div className="container">
          <div className="benefits-grid">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <div 
                  key={index}
                  className={`benefit-card ${isVisible ? 'visible' : ''}`}
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="benefit-icon-wrapper">
                    <Icon />
                  </div>
                  <h3>{benefit.title}</h3>
                  <p>{benefit.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section 
        ref={sectionRef}
        className={`trial-form-section ${isVisible ? 'visible' : ''}`}
      >
        <div className="container">
          <div className="form-section-wrapper">
            <div className="form-header">
              <div className="form-header-icon">
                <FaCalendarAlt />
              </div>
              <h2>Fill Out the Form</h2>
              <p>Complete the form below and we'll contact you within 24 hours to schedule your free trial class.</p>
            </div>

            <form ref={formRef} onSubmit={handleSubmit} className="trial-form" autoComplete="off">
              <div className="form-row">
                <div className="form-group">
                  <div className="input-wrapper">
                    <FaUser className="input-icon" />
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      onFocus={() => handleFocus('name')}
                      onBlur={() => handleBlur('name')}
                      autoComplete="off"
                      required
                    />
                    <label className={focused.name || formData.name ? 'focused' : ''}>
                      Full Name <span className="required">*</span>
                    </label>
                  </div>
                </div>

                <div className="form-group">
                  <div className="input-wrapper">
                    <FaEnvelope className="input-icon" />
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      onFocus={() => handleFocus('email')}
                      onBlur={() => handleBlur('email')}
                      autoComplete="off"
                      required
                    />
                    <label className={focused.email || formData.email ? 'focused' : ''}>
                      Email Address <span className="required">*</span>
                    </label>
                  </div>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <div className="input-wrapper">
                    <FaPhone className="input-icon" />
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      onFocus={() => handleFocus('phone')}
                      onBlur={() => handleBlur('phone')}
                      autoComplete="off"
                      required
                    />
                    <label className={focused.phone || formData.phone ? 'focused' : ''}>
                      Phone Number <span className="required">*</span>
                    </label>
                  </div>
                </div>

                <div className="form-group">
                  <div className="input-wrapper">
                    <FaGraduationCap className="input-icon" />
                    <select
                      name="studentAge"
                      value={formData.studentAge}
                      onChange={handleChange}
                      onFocus={() => handleFocus('studentAge')}
                      onBlur={() => handleBlur('studentAge')}
                      autoComplete="off"
                    >
                      <option value="5-10">5-10 years</option>
                      <option value="11-15">11-15 years</option>
                      <option value="16-20">16-20 years</option>
                      <option value="21-30">21-30 years</option>
                      <option value="31+">31+ years</option>
                    </select>
                    <label className={focused.studentAge || formData.studentAge ? 'focused' : ''}>
                      Student Age
                    </label>
                  </div>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <div className="input-wrapper">
                    <FaBookOpen className="input-icon" />
                    <select
                      name="course"
                      value={formData.course}
                      onChange={handleChange}
                      onFocus={() => handleFocus('course')}
                      onBlur={() => handleBlur('course')}
                      autoComplete="off"
                      required
                    >
                      {courses.map((course, index) => (
                        <option key={index} value={course.toLowerCase().replace(/\s+/g, '-')}>
                          {course}
                        </option>
                      ))}
                    </select>
                    <label className={focused.course || formData.course ? 'focused' : ''}>
                      Course Interest <span className="required">*</span>
                    </label>
                  </div>
                </div>

                <div className="form-group">
                  <div className="input-wrapper">
                    <FaClock className="input-icon" />
                    <select
                      name="preferredTime"
                      value={formData.preferredTime}
                      onChange={handleChange}
                      onFocus={() => handleFocus('preferredTime')}
                      onBlur={() => handleBlur('preferredTime')}
                      autoComplete="off"
                    >
                      <option value="morning">Morning (6 AM - 12 PM)</option>
                      <option value="afternoon">Afternoon (12 PM - 5 PM)</option>
                      <option value="evening">Evening (5 PM - 9 PM)</option>
                      <option value="night">Night (9 PM - 12 AM)</option>
                    </select>
                    <label className={focused.preferredTime || formData.preferredTime ? 'focused' : ''}>
                      Preferred Time
                    </label>
                  </div>
                </div>
              </div>

              <div className="form-group full-width">
                <div className="input-wrapper">
                  <FaBookOpen className="input-icon" />
                  <select
                    name="experience"
                    value={formData.experience}
                    onChange={handleChange}
                    onFocus={() => handleFocus('experience')}
                    onBlur={() => handleBlur('experience')}
                    autoComplete="off"
                  >
                    <option value="beginner">Beginner - No prior experience</option>
                    <option value="basic">Basic - Can read Arabic letters</option>
                    <option value="intermediate">Intermediate - Can read Quran</option>
                    <option value="advanced">Advanced - Want to improve</option>
                  </select>
                  <label className={focused.experience || formData.experience ? 'focused' : ''}>
                    Previous Experience
                  </label>
                </div>
              </div>

              <div className="form-submit">
                <button type="submit" className="submit-btn">
                  <span>Book Free Trial Class</span>
                  <FaChevronRight className="btn-arrow" />
                </button>
                <p className="form-note">
                  <FaCheckCircle className="note-icon" />
                  By submitting this form, you agree to be contacted by our team to schedule your free trial class.
                </p>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="trial-cta-section">
        <div className="container">
          <div className="cta-content">
            <h3>Have Questions?</h3>
            <p>Our team is here to help you get started on your Quran learning journey.</p>
            <div className="cta-links">
              <a href="/contact-us" className="cta-link">
                Contact Us
              </a>
              <a href="/courses" className="cta-link secondary">
                View All Courses
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FreeTrial;
